import { withMixpanel } from "@/events/mixpanelClient";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { fetchBaseData, useShowAds } from "@repo/shared/redux";
import jwt_decode, { JwtPayload } from "jwt-decode";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router";

import { routes } from "./Routes";
import { APIStatus } from "./api/APIStatus.type";
import { ROUTER_BASENAME } from "./config/localePath";
import { useAdGutter } from "./hooks/useAdGutter";
import { invalidateEvolutions } from "./redux/evolutions/evolutionsSlice";
import { loadedContentSelector } from "./redux/loadedContent/loadedContentSlice";
import { initSbcParams } from "./redux/sbcs/sbcFiltersSlice";
import { AppDispatch } from "./redux/store";
import {
  clearDidExplicitLogin,
  didExplicitLoginSelector,
  fetchPrices,
  fetchUser,
  getUserSelector,
} from "./redux/user/userSlice";
import { GROUP_ID } from "./settings";
import { safeRemoveItem } from "./utils/safeStorage";

/*
  Create the browser router. `basename` is the edition route base ("" for the
  root deploy, "/fc27" for the staged FC 27 deploy) followed by the URL's
  locale prefix ("/de"), which makes every Link and navigate() emit both
  without touching individual call sites. The edition part must match the
  Vite `base`; the locale part must not, since assets are locale-independent.
*/
const router = createBrowserRouter(routes, {
  basename: ROUTER_BASENAME || undefined,
});

/**
 * localStorage keys left over from experiments that no longer run: the
 * `price_format` test, the front-page feed-order test (retired 2026-08-12, won
 * by Trending on top), the club-import flow test (retired 2026-09-11, won by
 * the redesigned import screens), the social-proof banner test (retired
 * 2026-09-19, won by the roster strip), the club-import login timing test
 * (retired 2026-09-25, won by log in first) and the direct-to-checkout test
 * (retired 2026-09-26, won by the pricing page) — see experiments/RETIRE.md.
 */
const RETIRED_EXPERIMENT_KEYS = [
  GROUP_ID,
  "ACTIVATION-20260806-7484989986",
  "club_import_flow_ab",
  // Gated the daily test's enrollment event, which no longer fires.
  "daily_credit_ab_reported",
  "social_proof_banner_ab",
  // All three runs of the login timing test, plus the last run's expiry.
  "club_import_login_timing_ab",
  "club_import_login_vs_import_first_ab",
  "club_import_login_vs_import_first_v3_ab",
  "club_import_login_vs_import_first_v3_ab_expires_at",
  // First run of the squad builder filters test, restarted under _v2_ 2026-09-25.
  "squad_builder_filters_ab",
  "direct_checkout_ab",
  "squad_builder_filters_v2_ab",
  "gallery_ab",
  // Gated the gallery test's enrollment call.
  "gallery_enrolled",
];

/**
 * The retired tests again, as Mixpanel super properties.
 *
 * A separate list because it is a separate store: `mixpanel.register` writes
 * into the SDK's own cookie (`mp_<token>_mixpanel`), which `track` merges into
 * every event it sends. That store is persistent — deleting the `register` call
 * only stops NEW browsers from getting the property; every browser that ran the
 * old code keeps sending it, for the cookie's year, with no call involved.
 *
 * So a retired test needs both sweeps: the bucket above, and this. Without it
 * returning browsers stamp a dead test's arm on every event while fresh ones
 * stamp nothing, which quietly mixes the two populations in any report split on
 * that key.
 *
 * Cheap and idempotent — unregistering a property that was never there is a
 * no-op, so these stay until the cookies they target have aged out.
 */
const RETIRED_SUPER_PROPERTIES = [
  "ACTIVATION-20260806-7484989986", // feed order, retired 2026-08-12
  "ACTIVATION-20260708-1783508280", // front page, retired earlier
  "club_import_flow_ab", // club-import flow, retired 2026-09-11
  "social_proof_banner_ab", // social-proof banner, retired 2026-09-19
  "club_import_login_timing_ab", // login timing, first run, restarted 2026-09-23
  "club_import_login_vs_import_first_ab", // second run, restarted 2026-09-23
  "club_import_login_vs_import_first_v3_ab", // third run, retired 2026-09-25
  "squad_builder_filters_ab", // squad builder filters, first run, restarted 2026-09-25
  "direct_checkout_ab", // direct-to-checkout, retired 2026-09-26
  "squad_builder_filters_v2_ab", // squad builder filters, retired 2026-10-04
];

function App() {
  const { user, countryCode, prices } = useSelector(getUserSelector);
  const { versionsStatus } = useSelector(loadedContentSelector);
  const [shouldIdentifyUserForHotjar, setShouldIdentifyUserForHotjar] =
    useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const [baseDataFailed, setBaseDataFailed] = useState(0);
  const didExplicitLogin = useSelector(didExplicitLoginSelector);
  const showAds = useShowAds();

  // Publishes --ad-gutter for every spacer that has to clear the sticky ad.
  useAdGutter();

  useEffect(() => {
    dispatch(initSbcParams());
  }, [user.paid, dispatch]);

  useEffect(() => {
    // Disabled for now.
    // mixpanelTrack("user_landed");
    // Leftovers from retired experiments, cleaned up on landing. Both are out
    // of CLIENT_EXPERIMENTS, so neither reaches x-mp-test-groups any more — but
    // the super properties are still on every event a returning browser sends
    // until they are unregistered here.
    for (const key of RETIRED_EXPERIMENT_KEYS) safeRemoveItem(key);
    withMixpanel((mp) => {
      for (const prop of RETIRED_SUPER_PROPERTIES) mp.unregister(prop);
    });
  }, []);

  useEffect(() => {
    if (!prices.length) dispatch(fetchPrices());
  }, [prices.length]);

  useEffect(() => {
    const hotjarIdentifyUser = async () => {
      if (window.hj && shouldIdentifyUserForHotjar) {
        window.hj("identify", user.uuid || undefined, {
          country: countryCode || undefined,
          subscription:
            user?.subscription === "diamond"
              ? "ultimate"
              : user?.subscription === "gold"
                ? "elite"
                : user?.subscription === "silver"
                  ? "pro"
                  : "free",
          loggedIn: user?.uuid ? "true" : "false",
        });
        setShouldIdentifyUserForHotjar(false);
      }
    };
    const timeoutId = setTimeout(hotjarIdentifyUser, 10000);

    return () => clearTimeout(timeoutId);
  }, [user, countryCode]);

  useEffect(() => {
    if (localStorage.getItem("access_token")) {
      try {
        jwt_decode<JwtPayload>(localStorage.getItem("access_token"));
        dispatch(fetchUser({ didLogin: false }));
      } catch {
        localStorage.removeItem("access_token");
      }
    }
    (window as any).rewardful("ready", function () {});
  }, []);

  // Max 3 retries
  const fetchInitialData = async ({
    status,
    failed,
    incrementFailed,
    functionToCall,
  }: {
    status: APIStatus;
    failed: number;
    incrementFailed: (failed: number) => void;
    functionToCall: () => void;
  }) => {
    if (status === "idle") {
      functionToCall();
    } else if (status === "rejected") {
      setTimeout(() => {
        if (failed < 2) {
          functionToCall();
          incrementFailed(failed + 1);
        }
      }, 1000);
    }
  };

  // All start-up data in one request; versionsStatus tracks it (DEV-4013).
  useEffect(() => {
    fetchInitialData({
      status: versionsStatus,
      failed: baseDataFailed,
      incrementFailed: setBaseDataFailed,
      functionToCall: () => dispatch(fetchBaseData()),
    });
  }, [dispatch, versionsStatus, baseDataFailed]);

  useEffect(() => {
    if (didExplicitLogin) {
      dispatch(invalidateEvolutions());
      dispatch(clearDidExplicitLogin());
    }
  }, [didExplicitLogin, dispatch]);

  // Conditionally load ad script based on the ad-free entitlement
  useEffect(() => {
    const loadAdScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src*="scripts.pubnation.com"]',
      );
      if (existingScript) {
        return;
      }

      if (showAds) {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.setAttribute("data-noptimize", "1");
        script.setAttribute("data-cfasync", "false");
        script.src =
          "//scripts.pubnation.com/tags/52a0d22c-8e6a-4798-8661-b86731f0bbbe.js";
        document.head.appendChild(script);
      }
    };
    // Ads only on the production deploy. deploy-prod.yml sets
    // VITE_APP_ENVIRONMENT="prod"; staging/beta/fc24/dev each set their own
    // value, so a positive match keeps ads off everywhere else. (NOT
    // BASE_URL — that's Vite's base *path*, "/" in every environment.)
    if (import.meta.env.VITE_APP_ENVIRONMENT === "prod") {
      loadAdScript();
    }
  }, [showAds]);

  return <RouterProvider router={router} />;
}

export default App;
